import { motion } from "framer-motion";
import Magnetic from "@/components/Magnetic";

const About = () => {
  return (
    <section id="about" className="py-12 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-background to-transparent z-10" />

      <div className="max-w-6xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-16 items-start">
          {/* Left: Text */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="relative mb-4">
              <span className="font-display text-[9rem] leading-none font-bold text-pulse/5 select-none absolute -top-12 -left-6 pointer-events-none">
                01
              </span>
              <h2 className="font-display text-3xl md:text-4xl text-foreground relative z-10 pt-12">
                Our <em className="text-gradient-pulse not-italic">Mission</em>
              </h2>
            </div>

            <p className="font-body text-foreground-muted leading-relaxed mb-5 text-lg">
              Pulse Chat empowers businesses with elite AI-driven communication tools. We specialize in high-speed integration and intelligent automation.
            </p>
            <p className="font-body text-foreground-muted leading-relaxed mb-8">
              From sophisticated AI assistants to automated response systems, we build the technology that lets your business scale effortlessly while maintaining a human touch.
            </p>

            <div className="grid grid-cols-2 gap-6 mb-10">
              {[
                { label: "Focus", value: "Small Business" },
                { label: "Location", value: "Global / Remote" },
                { label: "Performance", value: "90+ Core Web Vitals" },
                { label: "Response Time", value: "< 24 Hours" },
              ].map((item, idx) => (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 * idx }}
                  key={item.label}
                  className="border-l-2 border-pulse/40 pl-4"
                >
                  <p className="font-body text-xs text-pulse/60 uppercase tracking-widest">{item.label}</p>
                  <p className="font-body text-sm text-foreground mt-1 font-medium">{item.value}</p>
                </motion.div>
              ))}
            </div>

            <Magnetic strength={0.2}>
              <a
                href="#contact"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-pulse/30 text-pulse text-sm font-body font-medium hover:bg-pulse hover:text-primary-foreground transition-all duration-500 shadow-pulse/10 hover:shadow-pulse/20"
              >
                Inquire Now
              </a>
            </Magnetic>
          </motion.div>

          {/* Right: How we work */}
          <div className="space-y-8 mt-12 md:mt-24">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="font-body text-xs uppercase tracking-[0.4em] text-pulse/80 mb-8 border-b border-pulse/20 pb-2 w-fit"
            >
              The Ecosystem
            </motion.p>
            {[
              {
                step: "Design & UX",
                title: "Conversion-First Strategy",
                desc: "We prioritize flows that convert. No bloat, no clutter, just pure speed and clarity for your customers.",
              },
              {
                step: "Integration",
                title: "Automated Scheduling",
                desc: "Sync your existing calendar. We build a hands-free booking experience that reduces administrative overhead.",
              },
              {
                step: "AI Intelligence",
                title: "Custom AI Assistants",
                desc: "Implement bots that navigate your site, answer client questions, and pre-qualify leads 24/7.",
              },
            ].map((item, i) => (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                key={i}
                className="relative pl-8 border-l border-border hover:border-pulse/40 transition-colors duration-500 group"
              >
                <div className="absolute left-[-5px] top-1 w-2.5 h-2.5 rounded-full border-2 border-border group-hover:border-pulse bg-background transition-colors duration-500" />
                <p className="font-body text-xs text-pulse mb-1 tracking-wide">{item.step}</p>
                <h3 className="font-display text-xl font-semibold text-foreground group-hover:text-pulse transition-colors">{item.title}</h3>
                <p className="font-body text-sm text-foreground-muted leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
